import React from 'react';
import { Draggable, DraggableProvided, DraggableStateSnapshot } from '@hello-pangea/dnd';
import { Bug, Lightbulb, Clock, User, Calendar, MessageSquare, GripVertical } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { RichTextContent } from '../ui/RichTextEditor';
import {
  Ticket,
  ticketPriorityLabels,
  ticketPriorityColors,
  ticketAreaLabels
} from '../../types/ticket';

interface KanbanTicketCardProps {
  ticket: Ticket;
  index: number;
  onClick: () => void;
}

// Shared component for rendering ticket card content in Kanban view
export const TicketKanbanContent: React.FC<{
  ticket: Ticket;
  onClick: () => void;
  provided: DraggableProvided;
  snapshot: DraggableStateSnapshot;
}> = ({ ticket, onClick, provided, snapshot }) => {
  const TypeIcon = ticket.tipo === 'bug' ? Bug : Lightbulb;

  return (
    <Card
      variant="glass"
      className={`
        group w-full cursor-pointer transition-all
        ${snapshot.isDragging
          ? 'shadow-xl border-blue-500/50 bg-blue-500/20 backdrop-blur-sm scale-[1.02]'
          : 'hover:shadow-lg hover:shadow-white/5 hover:border-white/20'
        }
      `}
      onClick={onClick}
    >
      <CardContent className="p-3">
        <div className="flex items-start gap-2">
          {/* Drag Handle */}
          <div
            {...provided.dragHandleProps}
            className={`
              flex-shrink-0 p-1 rounded
              hover:bg-white/10 transition-colors
              ${snapshot.isDragging ? 'cursor-grabbing' : 'cursor-grab'}
            `}
            style={{ touchAction: 'none' }}
            onClick={(e) => e.stopPropagation()}
          >
            <GripVertical className="w-4 h-4 text-foreground-tertiary" />
          </div>

          {/* Ticket Content */}
          <div className="flex-1 min-w-0">
            {/* Type icon and label */}
            <div className="flex items-center gap-1.5 mb-1.5">
              <TypeIcon
                className={`w-4 h-4 flex-shrink-0 ${ticket.tipo === 'bug' ? 'text-red-400' : 'text-yellow-400'}`}
              />
              <span className="text-xs font-medium text-foreground-secondary">
                {ticket.tipo === 'bug' ? 'Bug' : 'Melhoria'}
              </span>
            </div>

            {/* Title */}
            <h4 className="font-medium text-foreground-primary text-sm leading-tight mb-1 line-clamp-2">
              {ticket.titulo}
            </h4>

            {/* Description */}
            {ticket.descricao && (
              <div className="text-xs text-foreground-tertiary mb-2 line-clamp-2">
                <RichTextContent content={ticket.descricao} className="text-xs" />
              </div>
            )}

            {/* Badges */}
            <div className="flex flex-wrap gap-1 mb-2">
              <span
                className="px-1.5 py-0.5 text-xs font-medium rounded"
                style={{
                  backgroundColor: `${ticketPriorityColors[ticket.prioridade]}20`,
                  color: ticketPriorityColors[ticket.prioridade]
                }}
              >
                {ticketPriorityLabels[ticket.prioridade]}
              </span>
              <span className="px-1.5 py-0.5 text-xs font-medium bg-white/5 text-foreground-secondary rounded">
                {ticketAreaLabels[ticket.area]}
              </span>
            </div>

            {/* Footer info */}
            <div className="flex flex-wrap items-center gap-2 text-xs text-foreground-tertiary">
              {ticket.estimatedHours && (
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {ticket.estimatedHours}h
                </span>
              )}

              {ticket.responsavelId && typeof ticket.responsavelId === 'object' && (
                <span className="flex items-center gap-1">
                  <User className="w-3 h-3" />
                  {ticket.responsavelId.name}
                </span>
              )}

              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {new Date(ticket.createdAt).toLocaleDateString('pt-BR')}
              </span>

              {ticket.comments && ticket.comments.length > 0 && (
                <span className="flex items-center gap-1">
                  <MessageSquare className="w-3 h-3" />
                  {ticket.comments.length}
                </span>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export const KanbanTicketCard: React.FC<KanbanTicketCardProps> = ({
  ticket,
  index,
  onClick
}) => {
  return (
    <Draggable draggableId={ticket._id} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          className="mb-2 w-full"
        >
          <TicketKanbanContent
            ticket={ticket}
            onClick={onClick}
            provided={provided}
            snapshot={snapshot}
          />
        </div>
      )}
    </Draggable>
  );
};

// Function to create renderClone for Droppable
export const createRenderTicketClone = (
  tickets: Ticket[],
  onClick: () => void
) => {
  return (provided: DraggableProvided, snapshot: DraggableStateSnapshot, rubric: any) => {
    const ticket = tickets.find(t => t._id === rubric.draggableId);
    if (!ticket) return null;

    return (
      <div
        ref={provided.innerRef}
        {...provided.draggableProps}
        className="mb-2 w-80"
        style={{
          ...provided.draggableProps.style,
          width: '320px'
        }}
      >
        <TicketKanbanContent
          ticket={ticket}
          onClick={onClick}
          provided={provided}
          snapshot={snapshot}
        />
      </div>
    );
  };
};
